import { useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Mail, Send, CheckCircle, AlertCircle, Building2 } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validateForm = (form, t) => {
  const errs = {};
  if (!form.name.trim()) errs.name = t('contact.validation_name');
  if (!form.email.trim() || !EMAIL_REGEX.test(form.email)) errs.email = t('contact.validation_email');
  if (!form.message.trim()) errs.message = t('contact.validation_message');
  return errs;
};

const inputClass = (hasError) =>
  `h-12 rounded-xl border-white/10 bg-[#0E131F] text-[#F8FAFC] placeholder:text-[#64748B] focus:border-[#6366F1] focus:ring-[#6366F1] ${hasError ? 'border-red-400' : ''}`;

function FormField({ label, error, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#F8FAFC] mb-2" style={{ fontFamily: "'Manrope', sans-serif" }}>
        {label}
      </label>
      {children}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

function StatusMessage({ status }) {
  const { t } = useLanguage();
  if (status === 'success') {
    return (
      <div data-testid="contact-success-message" className="flex items-center gap-2 p-4 rounded-xl bg-[#10B981]/10 border border-[#10B981]/30">
        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
        <p className="text-sm text-[#10B981]">{t('contact.success')}</p>
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div data-testid="contact-error-message" className="flex items-center gap-2 p-4 rounded-xl bg-red-500/10 border border-red-500/30">
        <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
        <p className="text-sm text-red-400">{t('contact.error')}</p>
      </div>
    );
  }
  return null;
}

function ContactForm() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null); // 'success' | 'error'
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validateForm(form, t);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setLoading(true);
    setStatus(null);
    try {
      await axios.post(`${API}/contact`, form);
      setStatus('success');
      setForm({ name: '', email: '', message: '' });
      setErrors({});
    } catch {
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
      <FormField label={t('contact.name')} error={errors.name}>
        <Input
          data-testid="contact-name-input"
          placeholder={t('contact.name_placeholder')}
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className={inputClass(errors.name)}
        />
      </FormField>

      <FormField label={t('contact.email')} error={errors.email}>
        <Input
          data-testid="contact-email-input"
          type="email"
          placeholder={t('contact.email_placeholder')}
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className={inputClass(errors.email)}
        />
      </FormField>

      <FormField label={t('contact.message')} error={errors.message}>
        <Textarea
          data-testid="contact-message-input"
          placeholder={t('contact.message_placeholder')}
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          rows={6}
          className={`rounded-xl border-white/10 bg-[#0E131F] text-[#F8FAFC] placeholder:text-[#64748B] focus:border-[#6366F1] focus:ring-[#6366F1] ${errors.message ? 'border-red-400' : ''}`}
        />
      </FormField>

      <Button
        data-testid="contact-submit-button"
        type="submit"
        disabled={loading}
        className="h-12 px-8 rounded-full bg-[#6366F1] text-white font-bold text-sm hover:bg-white hover:text-[#080B10] transition-all w-full md:w-auto"
      >
        {loading ? (
          <>{t('contact.sending')}</>
        ) : (
          <>
            {t('contact.submit')}
            <Send className="ml-2 h-4 w-4" />
          </>
        )}
      </Button>

      <StatusMessage status={status} />
    </form>
  );
}

function ContactInfoCard() {
  const { t } = useLanguage();
  return (
    <div className="space-y-6">
      <div className="bg-[#0E131F] border border-slate-800/60 rounded-2xl md:rounded-3xl p-8 md:p-10">
        <div className="w-14 h-14 rounded-2xl bg-[#6366F1]/10 border border-[#6366F1]/25 flex items-center justify-center mb-6">
          <Mail className="h-7 w-7 text-[#6366F1]" />
        </div>
        <h3
          className="text-lg font-semibold text-[#F8FAFC] mb-2"
          style={{ fontFamily: "'Plus Jakarta Sans', 'Noto Sans TC', sans-serif" }}
        >
          {t('contact.email_label')}
        </h3>
        <a
          href="mailto:info@dzentlimited.com"
          data-testid="contact-info-email"
          className="text-[#6366F1] hover:underline text-base"
          style={{ fontFamily: "'Manrope', sans-serif" }}
        >
          info@dzentlimited.com
        </a>
      </div>

      <div className="bg-[#0E131F] border border-slate-800/60 rounded-2xl md:rounded-3xl p-8 md:p-10" data-testid="contact-company-info">
        <div className="w-14 h-14 rounded-2xl bg-[#6366F1]/10 border border-[#6366F1]/25 flex items-center justify-center mb-6">
          <Building2 className="h-7 w-7 text-[#6366F1]" />
        </div>
        <h3
          className="text-lg font-semibold text-[#F8FAFC] mb-3"
          style={{ fontFamily: "'Plus Jakarta Sans', 'Noto Sans TC', sans-serif" }}
        >
          {t('contact.company_label')}
        </h3>
        <p className="text-base font-medium text-[#F8FAFC]" style={{ fontFamily: "'Manrope', 'Noto Sans TC', sans-serif" }}>
          {t('contact.company_name')}
        </p>
        <p className="text-sm text-[#94A3B8] mt-1" style={{ fontFamily: "'Manrope', 'Noto Sans TC', sans-serif" }}>
          {t('contact.company_registered')}
        </p>
      </div>
    </div>
  );
}

export default function ContactPage() {
  const { t } = useLanguage();

  return (
    <div data-testid="contact-page" className="pt-20 bg-[#080B10]">
      <SEOHead path="/contact" />
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#6366F1] mb-4">
              {t('contact.email_label')}
            </p>
            <h1
              data-testid="contact-title"
              className="text-4xl md:text-5xl lg:text-6xl tracking-tighter font-bold text-[#F8FAFC] leading-tight"
              style={{ fontFamily: "'Plus Jakarta Sans', 'Noto Sans TC', sans-serif" }}
            >
              {t('contact.title')}
            </h1>
            <p className="mt-4 text-base md:text-lg text-[#94A3B8] max-w-xl mx-auto" style={{ fontFamily: "'Manrope', 'Noto Sans TC', sans-serif" }}>
              {t('contact.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-7">
              <ContactForm />
            </div>
            <div className="md:col-span-5">
              <ContactInfoCard />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
